/**
 * Scoring client-side d'une photo de document (netteté / contraste / cadrage)
 * + détection des marges (cadre document) pour crop live — zéro appel réseau.
 */

export interface CaptureScore {
  /** 0..100 — score global */
  score: number;
  sharpness: number;
  contrast: number;
  brightness: number;
  /** true si utilisable pour OCR */
  ok: boolean;
  reason?: 'blurry' | 'dark' | 'bright' | 'low_contrast' | 'too_small';
}

/**
 * Marges normalisées (0..1) depuis chaque bord de l'image.
 * Zone utile = [left .. 1-right] × [top .. 1-bottom].
 */
export interface DocumentMargins {
  left: number;
  top: number;
  right: number;
  bottom: number;
  /** 0..1 — confiance de la détection auto */
  confidence: number;
}

/** Cadre large par défaut (presque plein écran) — l’auto resserre ensuite. */
export const DEFAULT_MARGINS: DocumentMargins = {
  left: 0.04,
  top: 0.04,
  right: 0.04,
  bottom: 0.04,
  confidence: 0,
};

const MAX_SIDE = 640;
const DETECT_SIDE = 280;
const MIN_MARGIN = 0;
const MAX_MARGIN = 0.45;

function variance(values: ArrayLike<number>): number {
  let sum = 0;
  let sumSq = 0;
  const n = values.length;
  if (!n) return 0;
  for (let i = 0; i < n; i++) {
    const v = values[i];
    sum += v;
    sumSq += v * v;
  }
  const mean = sum / n;
  return sumSq / n - mean * mean;
}

/** Laplacien 3×3 simplifié sur luminance (netteté). */
function laplacianVar(gray: Float32Array, w: number, h: number): number {
  const out: number[] = [];
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const i = y * w + x;
      const v =
        -gray[i - w] - gray[i - 1] + 8 * gray[i] - gray[i + 1] - gray[i + w];
      out.push(v);
    }
  }
  return variance(out);
}

export async function scoreImageSource(
  source: Blob | HTMLVideoElement | HTMLCanvasElement | HTMLImageElement,
): Promise<CaptureScore> {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) {
    return { score: 0, sharpness: 0, contrast: 0, brightness: 0, ok: false, reason: 'too_small' };
  }

  let sw = 0;
  let sh = 0;
  if (source instanceof HTMLVideoElement) {
    sw = source.videoWidth;
    sh = source.videoHeight;
  } else if (source instanceof HTMLImageElement) {
    sw = source.naturalWidth || source.width;
    sh = source.naturalHeight || source.height;
  } else if (source instanceof HTMLCanvasElement) {
    sw = source.width;
    sh = source.height;
  } else {
    const bmp = await createImageBitmap(source);
    sw = bmp.width;
    sh = bmp.height;
    const scale = Math.min(1, MAX_SIDE / Math.max(sw, sh));
    canvas.width = Math.max(1, Math.round(sw * scale));
    canvas.height = Math.max(1, Math.round(sh * scale));
    ctx.drawImage(bmp, 0, 0, canvas.width, canvas.height);
    bmp.close?.();
    return scoreCanvas(ctx, canvas.width, canvas.height);
  }

  if (sw < 80 || sh < 80) {
    return { score: 0, sharpness: 0, contrast: 0, brightness: 0, ok: false, reason: 'too_small' };
  }
  const scale = Math.min(1, MAX_SIDE / Math.max(sw, sh));
  canvas.width = Math.max(1, Math.round(sw * scale));
  canvas.height = Math.max(1, Math.round(sh * scale));
  ctx.drawImage(source as CanvasImageSource, 0, 0, canvas.width, canvas.height);
  return scoreCanvas(ctx, canvas.width, canvas.height);
}

function scoreCanvas(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
): CaptureScore {
  const img = ctx.getImageData(0, 0, w, h);
  const { data } = img;
  const gray = new Float32Array(w * h);
  let brightSum = 0;
  for (let i = 0, p = 0; i < data.length; i += 4, p++) {
    // luminance Rec. 601
    const g = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    gray[p] = g;
    brightSum += g;
  }
  const brightness = brightSum / gray.length;
  const contrast = Math.sqrt(variance(gray));
  const sharpness = laplacianVar(gray, w, h);

  // Normalisation empirique (mobile photos de docs)
  const sharpN = Math.min(1, sharpness / 350);
  const contrastN = Math.min(1, contrast / 55);
  const brightN = 1 - Math.min(1, Math.abs(brightness - 128) / 100);

  const score = Math.round(
    100 * (0.55 * sharpN + 0.3 * contrastN + 0.15 * brightN),
  );

  let reason: CaptureScore['reason'];
  let ok = true;
  if (sharpness < 40) {
    ok = false;
    reason = 'blurry';
  } else if (contrast < 14) {
    ok = false;
    reason = 'low_contrast';
  } else if (brightness < 40) {
    ok = false;
    reason = 'dark';
  } else if (brightness > 230) {
    ok = false;
    reason = 'bright';
  }

  return {
    score: Math.max(0, Math.min(100, score)),
    sharpness: Math.round(sharpness),
    contrast: Math.round(contrast * 10) / 10,
    brightness: Math.round(brightness),
    ok,
    reason,
  };
}

function clampMargin(v: number): number {
  return Math.max(MIN_MARGIN, Math.min(MAX_MARGIN, v));
}

/** Normalise / borne un jeu de marges (garde une zone utile ≥ 18 %). */
export function sanitizeMargins(m: Partial<DocumentMargins> | null | undefined): DocumentMargins {
  let left = clampMargin(Number.isFinite(Number(m?.left)) ? Number(m?.left) : DEFAULT_MARGINS.left);
  let top = clampMargin(Number.isFinite(Number(m?.top)) ? Number(m?.top) : DEFAULT_MARGINS.top);
  let right = clampMargin(Number.isFinite(Number(m?.right)) ? Number(m?.right) : DEFAULT_MARGINS.right);
  let bottom = clampMargin(Number.isFinite(Number(m?.bottom)) ? Number(m?.bottom) : DEFAULT_MARGINS.bottom);
  if (left + right > 0.82) {
    const s = 0.82 / (left + right);
    left *= s;
    right *= s;
  }
  if (top + bottom > 0.82) {
    const s = 0.82 / (top + bottom);
    top *= s;
    bottom *= s;
  }
  return {
    left,
    top,
    right,
    bottom,
    confidence: Math.max(0, Math.min(1, Number(m?.confidence ?? 0))),
  };
}

/** Interpolation douce entre deux jeux de marges (live overlay). */
export function lerpMargins(a: DocumentMargins, b: DocumentMargins, t: number): DocumentMargins {
  const k = Math.max(0, Math.min(1, t));
  return sanitizeMargins({
    left: a.left + (b.left - a.left) * k,
    top: a.top + (b.top - a.top) * k,
    right: a.right + (b.right - a.right) * k,
    bottom: a.bottom + (b.bottom - a.bottom) * k,
    confidence: a.confidence + (b.confidence - a.confidence) * k,
  });
}

/**
 * Rectangle d'affichage `object-fit: contain` du flux vidéo dans la boîte UI.
 * Coordonnées en pixels CSS relatifs à la boîte (camBox).
 */
export function videoContainRect(
  videoW: number,
  videoH: number,
  boxW: number,
  boxH: number,
): { left: number; top: number; width: number; height: number } {
  if (videoW <= 0 || videoH <= 0 || boxW <= 0 || boxH <= 0) {
    return { left: 0, top: 0, width: boxW, height: boxH };
  }
  const vAr = videoW / videoH;
  const bAr = boxW / boxH;
  if (vAr > bAr) {
    const height = boxW / vAr;
    return { left: 0, top: (boxH - height) / 2, width: boxW, height };
  }
  const width = boxH * vAr;
  return { left: (boxW - width) / 2, top: 0, width, height: boxH };
}

/** Profil 1D : intensité de transition (saut de luminance + gradient) le long d’un axe. */
function buildAxisEdgeProfile(
  gray: Float32Array,
  w: number,
  h: number,
  axis: 'x' | 'y',
): Float32Array {
  const n = axis === 'x' ? w : h;
  const cross = axis === 'x' ? h : w;
  const profile = new Float32Array(n);
  const span = Math.max(2, Math.round(cross * 0.04));

  for (let i = 2; i < n - 2; i++) {
    let jump = 0;
    let grad = 0;
    let samples = 0;
    // Échantillonner le milieu de l’axe transversal (évite le bruit des coins).
    const c0 = Math.floor(cross * 0.18);
    const c1 = Math.ceil(cross * 0.82);
    for (let c = c0; c < c1; c += Math.max(1, Math.floor((c1 - c0) / 48))) {
      let a = 0;
      let b = 0;
      for (let k = 1; k <= span; k++) {
        if (axis === 'x') {
          a += gray[c * w + Math.max(0, i - k)];
          b += gray[c * w + Math.min(w - 1, i + k)];
          const gi = c * w + i;
          grad += Math.abs(gray[gi + 1] - gray[gi - 1]);
        } else {
          a += gray[Math.max(0, i - k) * w + c];
          b += gray[Math.min(h - 1, i + k) * w + c];
          const gi = i * w + c;
          grad += Math.abs(gray[gi + w] - gray[gi - w]);
        }
      }
      jump += Math.abs(b - a) / span;
      samples++;
    }
    if (samples) {
      profile[i] = (jump / samples) * 0.7 + (grad / samples) * 0.3;
    }
  }
  // Lissage 3-tap
  const smooth = new Float32Array(n);
  for (let i = 1; i < n - 1; i++) {
    smooth[i] = 0.25 * profile[i - 1] + 0.5 * profile[i] + 0.25 * profile[i + 1];
  }
  return smooth;
}

/** Premier pic fort depuis un bord (bord papier). */
function findEdgePeak(
  profile: Float32Array,
  fromStart: boolean,
): { idx: number; strength: number } {
  const n = profile.length;
  const search = Math.max(8, Math.floor(n * 0.42));
  let sum = 0;
  let cnt = 0;
  for (let i = 2; i < n - 2; i++) {
    sum += profile[i];
    cnt++;
  }
  const mean = cnt ? sum / cnt : 0;
  let varSum = 0;
  for (let i = 2; i < n - 2; i++) {
    const d = profile[i] - mean;
    varSum += d * d;
  }
  const std = Math.sqrt(varSum / Math.max(1, cnt));
  const thr = Math.max(8, mean + std * 0.85);

  let bestIdx = fromStart ? Math.round(n * 0.04) : Math.round(n * 0.96);
  let best = 0;

  if (fromStart) {
    for (let i = 3; i < search; i++) {
      const s = profile[i];
      if (s < thr) continue;
      if (s >= profile[i - 1] && s >= profile[i + 1] && s > best) {
        best = s;
        bestIdx = i;
      }
    }
  } else {
    for (let i = n - 4; i >= n - search; i--) {
      const s = profile[i];
      if (s < thr) continue;
      if (s >= profile[i - 1] && s >= profile[i + 1] && s > best) {
        best = s;
        bestIdx = i;
      }
    }
  }
  return { idx: bestIdx, strength: best };
}

/**
 * Détecte les marges du document via pics de transition luminance/gradient
 * depuis chaque bord (papier vs fond) — robuste table claire / doc blanc.
 */
export async function detectDocumentMargins(
  source: HTMLVideoElement | HTMLCanvasElement | HTMLImageElement | Blob,
): Promise<DocumentMargins> {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return { ...DEFAULT_MARGINS, confidence: 0 };

  let sw = 0;
  let sh = 0;
  let drawn = false;
  if (source instanceof HTMLVideoElement) {
    sw = source.videoWidth;
    sh = source.videoHeight;
  } else if (source instanceof HTMLImageElement) {
    sw = source.naturalWidth || source.width;
    sh = source.naturalHeight || source.height;
  } else if (source instanceof HTMLCanvasElement) {
    sw = source.width;
    sh = source.height;
  } else {
    const bmp = await createImageBitmap(source);
    sw = bmp.width;
    sh = bmp.height;
    const scale = Math.min(1, DETECT_SIDE / Math.max(sw, sh));
    canvas.width = Math.max(16, Math.round(sw * scale));
    canvas.height = Math.max(16, Math.round(sh * scale));
    ctx.drawImage(bmp, 0, 0, canvas.width, canvas.height);
    bmp.close?.();
    drawn = true;
  }
  if (!drawn) {
    if (sw < 40 || sh < 40) return { ...DEFAULT_MARGINS, confidence: 0 };
    const scale = Math.min(1, DETECT_SIDE / Math.max(sw, sh));
    canvas.width = Math.max(16, Math.round(sw * scale));
    canvas.height = Math.max(16, Math.round(sh * scale));
    ctx.drawImage(source as CanvasImageSource, 0, 0, canvas.width, canvas.height);
  }

  const w = canvas.width;
  const h = canvas.height;
  const { data } = ctx.getImageData(0, 0, w, h);
  const gray = new Float32Array(w * h);
  for (let i = 0, p = 0; i < data.length; i += 4, p++) {
    gray[p] = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
  }

  const colProf = buildAxisEdgeProfile(gray, w, h, 'x');
  const rowProf = buildAxisEdgeProfile(gray, w, h, 'y');

  const leftE = findEdgePeak(colProf, true);
  const rightE = findEdgePeak(colProf, false);
  const topE = findEdgePeak(rowProf, true);
  const bottomE = findEdgePeak(rowProf, false);

  let x0 = leftE.idx;
  let x1 = rightE.idx;
  let y0 = topE.idx;
  let y1 = bottomE.idx;

  // Ordre incohérent → pas de doc fiable.
  if (x1 - x0 < w * 0.22 || y1 - y0 < h * 0.22) {
    return { ...DEFAULT_MARGINS, confidence: 0.12 };
  }

  const strengths = [leftE.strength, rightE.strength, topE.strength, bottomE.strength];
  const minStr = Math.min(...strengths);
  const avgStr = strengths.reduce((a, b) => a + b, 0) / 4;
  // Confiance : pics nets + rectangle raisonnable.
  const covW = (x1 - x0 + 1) / w;
  const covH = (y1 - y0 + 1) / h;
  let confidence = 0;
  if (minStr >= 10 && avgStr >= 14 && covW >= 0.25 && covH >= 0.25 && covW <= 0.96 && covH <= 0.96) {
    confidence = Math.max(
      0.28,
      Math.min(0.97, 0.2 + Math.min(1, avgStr / 55) * 0.45 + (1 - Math.abs(0.55 - covW * covH)) * 0.25),
    );
  } else if (avgStr >= 10) {
    confidence = 0.22;
  } else {
    return { ...DEFAULT_MARGINS, confidence: 0.1 };
  }

  // Padding léger POUR INCLURE le bord papier (pas couper le texte).
  const padX = Math.max(1, Math.round(w * 0.012));
  const padY = Math.max(1, Math.round(h * 0.012));
  x0 = Math.max(0, x0 - padX);
  y0 = Math.max(0, y0 - padY);
  x1 = Math.min(w - 1, x1 + padX);
  y1 = Math.min(h - 1, y1 + padY);

  return sanitizeMargins({
    left: x0 / w,
    top: y0 / h,
    right: (w - 1 - x1) / w,
    bottom: (h - 1 - y1) / h,
    confidence,
  });
}

/** Capture une frame vidéo → JPEG Blob (optionnellement croppée aux marges). */
export async function captureVideoFrame(
  video: HTMLVideoElement,
  quality = 0.92,
  margins?: DocumentMargins | null,
): Promise<Blob> {
  const w = video.videoWidth;
  const h = video.videoHeight;
  if (!w || !h) throw new Error('caméra pas prête');
  const m = margins ? sanitizeMargins(margins) : null;
  const sx = m ? Math.round(m.left * w) : 0;
  const sy = m ? Math.round(m.top * h) : 0;
  const sw = m ? Math.max(32, Math.round(w * (1 - m.left - m.right))) : w;
  const sh = m ? Math.max(32, Math.round(h * (1 - m.top - m.bottom))) : h;
  const canvas = document.createElement('canvas');
  canvas.width = sw;
  canvas.height = sh;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas indisponible');
  ctx.drawImage(video, sx, sy, sw, sh, 0, 0, sw, sh);
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob((b) => resolve(b), 'image/jpeg', quality),
  );
  if (!blob) throw new Error('capture échouée');
  return blob;
}

/**
 * Sélection intelligente :
 * - mode single : meilleure prise (score max, ok préféré)
 * - mode multi : toutes les prises au-dessus du seuil, sinon top-N
 * - dédoublonnage burst : si 2 prises < 1.2s et scores proches, garde la meilleure
 */
export function selectShotsIntelligent<T extends {
  id: string;
  score: number;
  ok: boolean;
  takenAt: number;
}>(
  shots: T[],
  opts: { multi: boolean; minScore?: number; maxKeep?: number; burstMs?: number } = { multi: false },
): T[] {
  if (!shots.length) return [];
  const minScore = opts.minScore ?? 35;
  const maxKeep = opts.maxKeep ?? (opts.multi ? 10 : 1);
  const burstMs = opts.burstMs ?? 1200;

  // Dédup burst chronologique
  const ordered = [...shots].sort((a, b) => a.takenAt - b.takenAt);
  const deduped: T[] = [];
  for (const s of ordered) {
    const prev = deduped[deduped.length - 1];
    if (
      prev &&
      s.takenAt - prev.takenAt < burstMs &&
      Math.abs(s.score - prev.score) < 18
    ) {
      if (s.score > prev.score) deduped[deduped.length - 1] = s;
      continue;
    }
    deduped.push(s);
  }

  if (!opts.multi) {
    const best = [...deduped].sort((a, b) => {
      if (a.ok !== b.ok) return a.ok ? -1 : 1;
      return b.score - a.score;
    })[0];
    return best ? [best] : [];
  }

  let picked = deduped.filter((s) => s.ok && s.score >= minScore);
  if (!picked.length) {
    picked = [...deduped].sort((a, b) => b.score - a.score).slice(0, 1);
  } else {
    picked = [...picked].sort((a, b) => b.score - a.score).slice(0, maxKeep);
  }
  return picked;
}
